/**
 * FaceMesh — the sign-in artwork.
 * @file src/web/features/auth/components/FaceMesh.tsx
 *
 * @llm-rule WHEN: Decorating the auth screens' brand panel
 * @llm-rule NOTE: These are the same 68 landmarks the clock-in camera actually
 *   reads — jaw, brows, eyes, nose, lips, in face-api's ordering. The artwork
 *   is the product explaining itself, not a stock photo of a mountain.
 * @llm-rule AVOID: Adding an image dependency here. It is pure SVG so it scales
 *   to any panel, recolours with the theme, and costs no network request.
 */

/** Landmarks on a 100 × 110 canvas, in face-api's 68-point order. */
const JAW = [
  [14, 42], [15, 52], [17, 62], [19, 71], [23, 80], [29, 87], [36, 93], [43, 97],
  [50, 99], [57, 97], [64, 93], [71, 87], [77, 80], [81, 71], [83, 62], [85, 52], [86, 42],
];
const BROW_L = [[22, 34], [28, 30], [35, 29], [42, 30], [48, 33]];
const BROW_R = [[52, 33], [58, 30], [65, 29], [72, 30], [78, 34]];
const BRIDGE = [[50, 38], [50, 45], [50, 52], [50, 58]];
const NOSTRILS = [[43, 63], [46, 64.5], [50, 65.5], [54, 64.5], [57, 63]];
const EYE_L = [[28, 42], [32, 39], [37, 39], [41, 43], [37, 45.5], [32, 45.5]];
const EYE_R = [[59, 43], [63, 39], [68, 39], [72, 42], [68, 45.5], [63, 45.5]];
const LIP_OUTER = [
  [38, 76], [42, 73], [46, 72], [50, 73], [54, 72], [58, 73],
  [62, 76], [58, 81], [54, 83], [50, 84], [46, 83], [42, 81],
];
const LIP_INNER = [[41, 76], [46, 75], [50, 75.5], [54, 75], [59, 76], [54, 79], [50, 80], [46, 79]];

const OPEN_PATHS = [JAW, BROW_L, BROW_R, BRIDGE, NOSTRILS];
const CLOSED_PATHS = [EYE_L, EYE_R, LIP_OUTER, LIP_INNER];
const ALL_POINTS = [
  ...JAW, ...BROW_L, ...BROW_R, ...BRIDGE, ...NOSTRILS,
  ...EYE_L, ...EYE_R, ...LIP_OUTER, ...LIP_INNER,
];

const line = (pts: number[][]) => pts.map(([x, y]) => `${x},${y}`).join(' ');

/**
 * The mesh, drawn twice: once faint as the resting state, once bright and
 * clipped to a band that sweeps down the face. The bright copy and the visible
 * scan line share one animation class, so the light and what it reveals can
 * never drift apart.
 */
function Mesh({ bright }: { bright?: boolean }) {
  const stroke = bright ? 'oklch(0.98 0 0)' : 'oklch(1 0 0 / 0.28)';
  const dot = bright ? 'oklch(0.99 0 0)' : 'oklch(1 0 0 / 0.45)';

  return (
    <g fill="none" stroke={stroke} strokeWidth={bright ? 0.7 : 0.55} strokeLinecap="round">
      {OPEN_PATHS.map((pts, i) => (
        <polyline key={`o${i}`} points={line(pts)} />
      ))}
      {CLOSED_PATHS.map((pts, i) => (
        <polygon key={`c${i}`} points={line(pts)} />
      ))}
      {ALL_POINTS.map(([x, y], i) => (
        <circle
          key={`p${i}`}
          cx={x}
          cy={y}
          r={bright ? 0.95 : 0.7}
          fill={dot}
          stroke="none"
          className={bright ? undefined : 'mesh-breathe'}
          style={bright ? undefined : { ['--d' as string]: `${(i % 12) * 140}ms` }}
        />
      ))}
    </g>
  );
}

export function FaceMesh({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 110"
      className={className}
      role="img"
      aria-label="A face mesh being scanned"
    >
      <defs>
        {/* The band of light. Everything inside it is the bright mesh. */}
        <clipPath id="mesh-band">
          <rect className="mesh-sweep" x={-10} y={-8} width={120} height={16} />
        </clipPath>

        <linearGradient id="scan-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="oklch(0.85 0.14 190)" stopOpacity="0" />
          <stop offset="50%" stopColor="oklch(0.92 0.16 190)" stopOpacity="0.95" />
          <stop offset="100%" stopColor="oklch(0.85 0.14 190)" stopOpacity="0" />
        </linearGradient>

        <linearGradient id="scan-glow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="oklch(0.9 0.15 190)" stopOpacity="0" />
          <stop offset="100%" stopColor="oklch(0.9 0.15 190)" stopOpacity="0.3" />
        </linearGradient>

        {/*
          Without this the band ends in two hard vertical edges and reads as a
          rectangle sliding over a face rather than as light. The mask sits on a
          static wrapper so it stays put while the band travels through it.
        */}
        <linearGradient id="scan-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#000" />
          <stop offset="16%" stopColor="#fff" />
          <stop offset="84%" stopColor="#fff" />
          <stop offset="100%" stopColor="#000" />
        </linearGradient>
        <mask id="scan-mask" maskUnits="userSpaceOnUse" x="0" y="-30" width="100" height="170">
          <rect x="0" y="-30" width="100" height="170" fill="url(#scan-fade)" />
        </mask>
      </defs>

      {/* Viewfinder: a ring, and brackets at the corners of the frame. */}
      <ellipse
        className="ring-pulse"
        cx={50}
        cy={62}
        rx={44}
        ry={50}
        fill="none"
        stroke="oklch(1 0 0 / 0.22)"
        strokeWidth={0.5}
        strokeDasharray="3 5"
      />
      <g stroke="oklch(1 0 0 / 0.4)" strokeWidth={0.9} fill="none" strokeLinecap="round">
        <polyline points="6,22 6,12 16,12" />
        <polyline points="94,22 94,12 84,12" />
        <polyline points="6,88 6,98 16,98" />
        <polyline points="94,88 94,98 84,98" />
      </g>

      <Mesh />

      <g clipPath="url(#mesh-band)">
        <Mesh bright />
      </g>

      {/* The scan line itself, on the same clock as the band above. */}
      <g mask="url(#scan-mask)">
        <g className="mesh-sweep">
          <rect x={0} y={-14} width={100} height={14} fill="url(#scan-glow)" />
          <rect x={0} y={-0.35} width={100} height={0.7} fill="url(#scan-line)" />
        </g>
      </g>
    </svg>
  );
}
